import type { RegistryConnection } from "@/types";

const INTEGRATION_CONFIGS = {
  GREXEL: "Grexel",
  ELERING: "Elering (Estonia)",
  FINEXTRA: "Finextra (Finland)",
  CESAR: "Cesar (Sweden)",
  EEX: "EEX (France)",
  CERTIGY: "NECS (Norway)",
  DAPEEP: "DAPEEP (Greece)",
  "GSE-ITALY": "GSE (Italy)",
  "BELGIUM-FLANDERS": "Flanders (Belgium)",
  "BELGIUM-BRUGEL": "Brussels (Belgium)",
  "BELGIUM-WALLONIA": "Wallonia (Belgium)",
  "HKNR-GERMANY": "HKNR (Germany)",
  EEGO: "EEGO REN (Portugal)",
  VERTICER: "VERTICER (Netherlands)",
  TGE: "TGE (Poland)",
  OFGEM: "OFGEM (United Kingdom)",
  EVIDENT: "Evident (Turkey)",
  SEDA: "SEDA (Bulgaria)",
  CNMC: "CNMC (Spain)",
} as const;

export type IntegrationName = keyof typeof INTEGRATION_CONFIGS;

export const AVAILABLE_INTEGRATIONS: IntegrationName[] = Object.keys(
  INTEGRATION_CONFIGS
) as IntegrationName[];

export function formatIntegrationName(integrationName: string): string {
  return (
    INTEGRATION_CONFIGS[integrationName as IntegrationName] || integrationName
  );
}

type TransactionLogFields = Pick<
  RegistryConnection,
  | "integration_name"
  | "transaction_log_rule_exclusion"
  | "transaction_log_disabled"
>;

/**
 * Whether the connection ingests its registry transaction log: no rule excludes it and an
 * admin has not disabled it.
 */
export function transactionLogApplies(
  connection: TransactionLogFields
): boolean {
  return (
    connection.transaction_log_rule_exclusion === null &&
    !connection.transaction_log_disabled
  );
}

/**
 * Admin-facing explanation of why a registry connection is outside the transaction log, or null
 * when the log applies to it. A rule exclusion is reported ahead of the admin's disable switch.
 */
export function transactionLogOffReason(
  connection: TransactionLogFields
): string | null {
  switch (connection.transaction_log_rule_exclusion) {
    case "REGISTRY_UNSUPPORTED":
      return `Transaction logs are not enabled for ${formatIntegrationName(connection.integration_name)} registry`;
    case "HOSTED_ACCOUNT":
      return "Transaction logs are not enabled for hosted accounts";
    case "DEVICE_TRANSFER":
      return "Transaction logs are not enabled for device transfer connections";
    case "SOLDERA_OWN_ACCOUNT":
      return "Transaction logs are not enabled for Soldera's own accounts";
    default:
      return connection.transaction_log_disabled
        ? "Transaction log disabled for this connection"
        : null;
  }
}
